import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Activity, RefreshCw, Loader, Search, Info, Box,
  RotateCcw, ZoomIn, ZoomOut, Eye, Sliders, Download,
  BookOpen, HelpCircle, X, Lightbulb, MousePointer, TrendingUp
} from 'lucide-react';

/**
 * 3D Volatility Surface Visualization
 * 
 * Displays implied volatility across strikes and expirations
 * in an interactive 3D surface plot.
 * 
 * X-axis: Strike Price
 * Y-axis: Days to Expiration
 * Z-axis: Implied Volatility
 */

const VolatilitySurface3D = ({ symbol: initialSymbol }) => {
  const [currentSymbol, setCurrentSymbol] = useState(initialSymbol || 'SPY');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const [surfaceData, setSurfaceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [viewMode, setViewMode] = useState('3d'); // '3d', 'heatmap', 'skew'
  const [selectedExpiration, setSelectedExpiration] = useState(null);
  const [showControls, setShowControls] = useState(false);
  
  // 3D camera controls
  const [rotation, setRotation] = useState({ x: -30, y: 45 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Mobile and education state
  const [isMobile, setIsMobile] = useState(false);
  const [showEducation, setShowEducation] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (currentSymbol) {
      fetchVolatilitySurface();
    }
  }, [currentSymbol]);

  const fetchVolatilitySurface = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/options/volatility-surface/${currentSymbol}`);
      const data = await response.json();
      
      console.log('Volatility surface response:', data);
      
      if (data.success) {
        // Transform data if needed - handle different API response formats
        const transformedData = transformSurfaceData(data);
        setSurfaceData(transformedData);
        if (transformedData.expirations?.length > 0) {
          setSelectedExpiration(transformedData.expirations[0]);
        }
      } else {
        setError(data.error || 'Failed to fetch volatility surface');
        // Use demo data on error
        setSurfaceData(generateDemoSurface(currentSymbol));
      }
    } catch (err) {
      console.error('Volatility surface error:', err);
      setError('Failed to load volatility surface data');
      // Use demo data on error
      setSurfaceData(generateDemoSurface(currentSymbol));
    } finally {
      setLoading(false);
    }
  };

  // Transform API data to expected format
  const transformSurfaceData = (data) => {
    // If data already has proper surface format, return as-is
    if (data.surface && Array.isArray(data.surface) && data.surface[0]?.ivs) {
      return data;
    }
    
    // If data has points array (alternative format from backend)
    if (data.points && Array.isArray(data.points)) {
      // Group points by expiration
      const byExpiration = {};
      const allStrikes = new Set();
      
      data.points.forEach(point => {
        const exp = point.expiration;
        if (!byExpiration[exp]) {
          byExpiration[exp] = { expiration: exp, dte: point.dte, strikes: {}, ivs: [] };
        }
        byExpiration[exp].strikes[point.strike] = point.iv;
        allStrikes.add(point.strike);
      });
      
      const sortedStrikes = Array.from(allStrikes).sort((a, b) => a - b);
      const surface = Object.values(byExpiration)
        .sort((a, b) => a.dte - b.dte)
        .map(row => ({
          expiration: row.expiration,
          dte: row.dte,
          ivs: sortedStrikes.map(strike => row.strikes[strike] || 0)
        }));
      
      return {
        ...data,
        surface,
        strikes: sortedStrikes,
        expirations: surface.map(s => s.expiration)
      };
    }
    
    // Fallback to demo data if format is unrecognized
    console.warn('Unrecognized data format, using demo data');
    return generateDemoSurface(data.symbol || currentSymbol);
  };

  // Generate demo surface data
  const generateDemoSurface = (symbol) => {
    const basePrice = symbol === 'SPY' ? 600 : symbol === 'QQQ' ? 520 : 150;
    const strikes = [];
    const surface = [];
    const expirations = [];
    
    // Generate strikes from 80% to 120% of price
    for (let i = -10; i <= 10; i++) {
      strikes.push(Math.round(basePrice * (1 + i * 0.02)));
    }
    
    // Generate expirations (7, 14, 21, 30, 45, 60, 90 days)
    const dtes = [7, 14, 21, 30, 45, 60, 90];
    
    dtes.forEach(dte => {
      const expDate = new Date();
      expDate.setDate(expDate.getDate() + dte);
      const expStr = expDate.toISOString().split('T')[0];
      expirations.push(expStr);
      
      // Generate IVs with smile and term structure
      const ivs = strikes.map(strike => {
        const moneyness = strike / basePrice;
        // Base IV around 20-25%
        let iv = 0.22;
        // Add smile effect (higher IV for OTM options)
        iv += Math.abs(moneyness - 1) * 0.15;
        // Add term structure (longer dated = slightly higher IV)
        iv += (dte / 365) * 0.05;
        // Add some randomness
        iv += (Math.random() - 0.5) * 0.02;
        return Math.max(0.1, iv);
      });
      
      surface.push({
        expiration: expStr,
        dte,
        ivs
      });
    });
    
    return {
      success: true,
      symbol,
      currentPrice: basePrice,
      surface,
      strikes,
      expirations,
      isDemo: true
    };
  };

  const searchStocks = async (query) => {
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/stocks/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(data || []);
      setShowSearchResults(true);
    } catch (err) {
      setSearchResults([]);
    }
  };

  const selectStock = (symbol) => {
    setCurrentSymbol(symbol);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Calculate surface statistics with proper null checks
  const surfaceStats = useMemo(() => {
    if (!surfaceData?.surface || !Array.isArray(surfaceData.surface)) {
      return null;
    }
    
    // Safely extract all IVs with null checks
    const allIVs = surfaceData.surface
      .filter(row => row && Array.isArray(row.ivs))
      .flatMap(row => row.ivs.filter(iv => typeof iv === 'number' && iv > 0));
    
    if (allIVs.length === 0) {
      return null;
    }
    
    const minIV = Math.min(...allIVs);
    const maxIV = Math.max(...allIVs);
    const avgIV = allIVs.reduce((a, b) => a + b, 0) / allIVs.length;
    
    return {
      minIV: minIV * 100,
      maxIV: maxIV * 100,
      avgIV: avgIV * 100,
      ivRange: (maxIV - minIV) * 100
    };
  }, [surfaceData]);

  // Mouse/Touch handlers for 3D rotation
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    setDragStart({ x: clientX, y: clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    const dx = clientX - dragStart.x;
    const dy = clientY - dragStart.y;
    
    setRotation(prev => ({
      x: Math.max(-90, Math.min(90, prev.x + dy * 0.5)),
      y: prev.y + dx * 0.5
    }));
    
    setDragStart({ x: clientX, y: clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.5, Math.min(2, prev * delta)));
  };

  const resetView = () => {
    setRotation({ x: -30, y: 45 });
    setZoom(1);
  };

  // Get color based on IV value
  const getIVColor = (iv, minIV, maxIV) => {
    if (maxIV === minIV) return 'rgb(59, 130, 246)';
    const normalized = (iv - minIV) / (maxIV - minIV);
    
    // Color gradient: blue (low) -> green -> yellow -> red (high)
    if (normalized < 0.25) {
      const t = normalized / 0.25;
      return `rgb(${Math.floor(59 + t * (16 - 59))}, ${Math.floor(130 + t * (185 - 130))}, ${Math.floor(246 + t * (129 - 246))})`;
    } else if (normalized < 0.5) {
      const t = (normalized - 0.25) / 0.25;
      return `rgb(${Math.floor(16 + t * (234 - 16))}, ${Math.floor(185 + t * (179 - 185))}, ${Math.floor(129 + t * (8 - 129))})`;
    } else if (normalized < 0.75) {
      const t = (normalized - 0.5) / 0.25;
      return `rgb(${Math.floor(234 + t * (249 - 234))}, ${Math.floor(179 + t * (115 - 179))}, ${Math.floor(8 + t * (22 - 8))})`;
    } else {
      const t = (normalized - 0.75) / 0.25;
      return `rgb(${Math.floor(249 + t * (239 - 249))}, ${Math.floor(115 + t * (68 - 115))}, ${Math.floor(22 + t * (68 - 22))})`;
    }
  };

  if (loading && !surfaceData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4rem',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
        borderRadius: '1rem',
        minHeight: '500px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader size={48} color="#a78bfa" className="spin" />
          <p style={{ color: '#a78bfa', marginTop: '1rem' }}>Loading Volatility Surface...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
      borderRadius: isMobile ? '0.75rem' : '1rem',
      overflow: 'hidden',
      border: '1px solid rgba(139, 92, 246, 0.2)'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
        borderBottom: '1px solid rgba(139, 92, 246, 0.3)',
        padding: isMobile ? '1rem' : '1.5rem 2rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '1rem', flexDirection: isMobile ? 'column' : 'row' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
              }}>
                <Box size={isMobile ? 22 : 28} color="white" />
              </div>
              <div>
                <h2 style={{
                  fontSize: isMobile ? '1.25rem' : '1.75rem',
                  fontWeight: '700',
                  margin: 0,
                  background: 'linear-gradient(135deg, #fff 0%, #a78bfa 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  3D Volatility Surface
                </h2>
                <p style={{ color: '#94a3b8', fontSize: isMobile ? '0.75rem' : '0.875rem', margin: 0 }}>
                  {currentSymbol} - Interactive IV Visualization
                </p>
              </div>
            </div>
            
            {/* Live badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem',
              padding: '0.375rem 0.75rem',
              background: surfaceData?.isDemo ? 'rgba(251, 191, 36, 0.15)' : 'rgba(16, 185, 129, 0.15)',
              border: `1px solid ${surfaceData?.isDemo ? 'rgba(251, 191, 36, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
              borderRadius: '2rem',
              fontSize: '0.7rem',
              fontWeight: '600',
              color: surfaceData?.isDemo ? '#fbbf24' : '#10b981'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                background: surfaceData?.isDemo ? '#fbbf24' : '#10b981',
                borderRadius: '50%',
                boxShadow: `0 0 8px ${surfaceData?.isDemo ? '#fbbf24' : '#10b981'}`
              }} />
              {surfaceData?.isDemo ? 'DEMO DATA' : 'LIVE IV DATA'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Learn Button */}
            <button
              onClick={() => setShowEducation(!showEducation)}
              style={{
                padding: isMobile ? '0.5rem' : '0.625rem 1rem',
                background: showEducation ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: `1px solid ${showEducation ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '0.5rem',
                color: showEducation ? '#10b981' : '#94a3b8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              <HelpCircle size={16} />
              {!isMobile && 'Learn'}
            </button>
            
            <button
              onClick={() => setShowControls(!showControls)}
              style={{
                padding: isMobile ? '0.5rem' : '0.625rem 1rem',
                background: showControls ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: `1px solid ${showControls ? 'rgba(139, 92, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '0.5rem',
                color: showControls ? '#a78bfa' : '#94a3b8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              <Sliders size={16} />
              {!isMobile && 'Controls'}
            </button>
            
            <button
              onClick={fetchVolatilitySurface}
              disabled={loading}
              style={{
                padding: isMobile ? '0.5rem' : '0.625rem 1rem',
                background: 'rgba(139, 92, 246, 0.2)',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                borderRadius: '0.5rem',
                color: '#a78bfa',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              {!isMobile && 'Refresh'}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div style={{ marginTop: '1rem', position: 'relative', maxWidth: isMobile ? '100%' : '400px' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#64748b'
          }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchStocks(e.target.value);
            }}
            placeholder="Search symbol..."
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.75rem',
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(100, 116, 139, 0.3)',
              borderRadius: '0.5rem',
              color: 'white',
              fontSize: '0.9rem',
              outline: 'none'
            }}
          />
          
          {showSearchResults && searchResults.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '0.5rem',
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '0.5rem',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 100
            }}>
              {searchResults.map((result, idx) => (
                <div
                  key={idx}
                  onClick={() => selectStock(result.symbol)}
                  style={{
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    borderBottom: idx < searchResults.length - 1 ? '1px solid #334155' : 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#334155'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ fontWeight: '600', color: '#60a5fa' }}>{result.symbol}</div>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{result.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Controls Panel */}
      {showControls && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          borderBottom: '1px solid rgba(71, 85, 105, 0.5)',
          padding: isMobile ? '0.75rem 1rem' : '1rem 2rem'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '0.75rem' : '1rem', alignItems: 'center' }}>
            {/* View Mode */}
            <div>
              <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}>
                View Mode
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {['3d', 'heatmap', 'skew'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    style={{
                      padding: isMobile ? '0.375rem 0.75rem' : '0.5rem 1rem',
                      background: viewMode === mode ? '#8b5cf6' : 'rgba(71, 85, 105, 0.5)',
                      border: 'none',
                      borderRadius: '0.375rem',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: isMobile ? '0.7rem' : '0.8rem',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}
                  >
                    {mode === '3d' ? '3D' : mode === 'heatmap' ? 'Heat' : 'Skew'}
                  </button>
                ))}
              </div>
            </div>

            {/* 3D Controls */}
            {viewMode === '3d' && (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    onClick={() => setZoom(prev => Math.min(2, prev * 1.2))}
                    style={{
                      padding: '0.5rem',
                      background: 'rgba(71, 85, 105, 0.5)',
                      border: 'none',
                      borderRadius: '0.375rem',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <ZoomIn size={18} />
                  </button>
                  <button
                    onClick={() => setZoom(prev => Math.max(0.5, prev * 0.8))}
                    style={{
                      padding: '0.5rem',
                      background: 'rgba(71, 85, 105, 0.5)',
                      border: 'none',
                      borderRadius: '0.375rem',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <ZoomOut size={18} />
                  </button>
                  <button
                    onClick={resetView}
                    style={{
                      padding: '0.5rem',
                      background: 'rgba(71, 85, 105, 0.5)',
                      border: 'none',
                      borderRadius: '0.375rem',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <RotateCcw size={18} />
                  </button>
                </div>
                
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {isMobile ? 'Touch & drag to rotate' : 'Drag to rotate • Scroll to zoom'}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Education Panel */}
      {showEducation && (
        <VolSurfaceEducation onClose={() => setShowEducation(false)} isMobile={isMobile} />
      )}

      {/* Main Content */}
      {surfaceData && (
        <div style={{ padding: isMobile ? '1rem' : '1.5rem 2rem' }}>
          {/* Stats Row */}
          {surfaceStats && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: isMobile ? '0.5rem' : '1rem',
              marginBottom: '1.5rem'
            }}>
              <StatCard label="Current Price" value={`$${surfaceData.currentPrice?.toFixed(2) || 'N/A'}`} color="#60a5fa" isMobile={isMobile} />
              <StatCard label="Min IV" value={`${surfaceStats.minIV.toFixed(1)}%`} color="#10b981" isMobile={isMobile} />
              <StatCard label="Max IV" value={`${surfaceStats.maxIV.toFixed(1)}%`} color="#ef4444" isMobile={isMobile} />
              <StatCard label="Avg IV" value={`${surfaceStats.avgIV.toFixed(1)}%`} color="#f59e0b" isMobile={isMobile} />
              {!isMobile && <StatCard label="IV Range" value={`${surfaceStats.ivRange.toFixed(1)}%`} color="#8b5cf6" isMobile={isMobile} />}
            </div>
          )}

          {/* Visualization */}
          {viewMode === '3d' && (
            <Surface3DView
              data={surfaceData}
              rotation={rotation}
              zoom={zoom}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleWheel}
              getIVColor={getIVColor}
              surfaceStats={surfaceStats}
              isMobile={isMobile}
              hoveredPoint={hoveredPoint}
              setHoveredPoint={setHoveredPoint}
            />
          )}

          {viewMode === 'heatmap' && (
            <HeatmapView
              data={surfaceData}
              getIVColor={getIVColor}
              surfaceStats={surfaceStats}
              isMobile={isMobile}
              hoveredPoint={hoveredPoint}
              setHoveredPoint={setHoveredPoint}
            />
          )}

          {viewMode === 'skew' && (
            <SkewView
              data={surfaceData}
              selectedExpiration={selectedExpiration}
              setSelectedExpiration={setSelectedExpiration}
              isMobile={isMobile}
              hoveredPoint={hoveredPoint}
              setHoveredPoint={setHoveredPoint}
            />
          )}

          {/* Hovered Point Tooltip */}
          {hoveredPoint && (
            <div style={{
              position: 'fixed',
              bottom: isMobile ? '80px' : '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(15, 23, 42, 0.95)',
              border: '1px solid rgba(139, 92, 246, 0.5)',
              borderRadius: '0.75rem',
              padding: '0.75rem 1rem',
              zIndex: 1000,
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                Strike: <span style={{ color: '#60a5fa', fontWeight: '600' }}>${hoveredPoint.strike?.toFixed(0)}</span>
              </div>
              {hoveredPoint.dte !== undefined && (
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  DTE: <span style={{ color: '#f59e0b', fontWeight: '600' }}>{hoveredPoint.dte}d</span>
                </div>
              )}
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                IV: <span style={{ color: '#10b981', fontWeight: '600' }}>{(hoveredPoint.iv * 100).toFixed(1)}%</span>
              </div>
              <button
                onClick={() => setHoveredPoint(null)}
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#ef4444'
                }}
              >
                <X size={12} />
              </button>
            </div>
          )}

          {/* Legend */}
          <div style={{
            marginTop: '1.5rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '1rem',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', color: '#94a3b8' }}>Low IV</span>
            <div style={{
              width: isMobile ? '120px' : '200px',
              height: isMobile ? '10px' : '12px',
              background: 'linear-gradient(90deg, #3b82f6 0%, #10b981 25%, #eab308 50%, #f97316 75%, #ef4444 100%)',
              borderRadius: '6px'
            }} />
            <span style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', color: '#94a3b8' }}>High IV</span>
          </div>

          {/* Education */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '0.5rem'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#93c5fd', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Info size={16} />
              Understanding the Volatility Surface
            </h4>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.8rem', color: '#cbd5e1', lineHeight: '1.6' }}>
              <li><strong>Volatility Smile:</strong> IV tends to be higher for deep ITM and OTM options (U-shape)</li>
              <li><strong>Volatility Skew:</strong> Put IVs often higher than call IVs (fear premium)</li>
              <li><strong>Term Structure:</strong> Near-term options often have higher IV before events</li>
              <li><strong>Trading Edge:</strong> Look for mispriced options where IV deviates from the surface</li>
            </ul>
          </div>
        </div>
      )}

      {error && !surfaceData && (
        <div style={{
          margin: '2rem',
          padding: '2rem',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '0.75rem',
          textAlign: 'center'
        }}>
          <Activity size={48} color="#ef4444" style={{ marginBottom: '1rem' }} />
          <p style={{ color: '#f87171', margin: 0 }}>{error}</p>
        </div>
      )}
    </div>
  );
};

// ============================================
// SUB-COMPONENTS
// ============================================

const StatCard = ({ label, value, color, isMobile }) => (
  <div style={{
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid rgba(71, 85, 105, 0.5)',
    borderRadius: '0.5rem',
    padding: isMobile ? '0.75rem' : '1rem'
  }}>
    <div style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{label}</div>
    <div style={{ fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: '700', color }}>{value}</div>
  </div>
);

const Surface3DView = ({ data, rotation, zoom, onMouseDown, onMouseMove, onMouseUp, onMouseLeave, onWheel, getIVColor, surfaceStats, isMobile, hoveredPoint, setHoveredPoint }) => {
  if (!data?.surface || !surfaceStats) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
        No surface data available
      </div>
    );
  }

  const { surface, strikes, currentPrice } = data;
  const { minIV, maxIV } = surfaceStats;

  // SVG-based 3D projection - responsive sizing
  const width = isMobile ? 400 : 800;
  const height = isMobile ? 350 : 500;
  const centerX = width / 2;
  const centerY = height / 2;

  // Project 3D point to 2D
  const project = (x, y, z) => {
    const scale = 150 * zoom;
    const radX = (rotation.x * Math.PI) / 180;
    const radY = (rotation.y * Math.PI) / 180;

    // Rotate around Y axis
    const x1 = x * Math.cos(radY) - z * Math.sin(radY);
    const z1 = x * Math.sin(radY) + z * Math.cos(radY);

    // Rotate around X axis
    const y1 = y * Math.cos(radX) - z1 * Math.sin(radX);
    const z2 = y * Math.sin(radX) + z1 * Math.cos(radX);

    // Perspective projection
    const perspective = 500;
    const factor = perspective / (perspective + z2);

    return {
      x: centerX + x1 * scale * factor,
      y: centerY - y1 * scale * factor,
      z: z2
    };
  };

  // Generate surface points with null checks
  const points = [];
  const validSurface = surface.filter(row => row && Array.isArray(row.ivs));
  const numStrikes = Math.min(strikes?.length || 0, 20);
  const numExps = Math.min(validSurface.length, 8);

  for (let i = 0; i < numExps; i++) {
    const row = validSurface[i];
    if (!row?.ivs) continue;

    for (let j = 0; j < numStrikes && j < row.ivs.length; j++) {
      const iv = row.ivs[j] || 0;
      if (iv <= 0) continue;

      // Normalize coordinates to -1 to 1 range
      const x = numStrikes > 1 ? (j / (numStrikes - 1)) * 2 - 1 : 0;
      const y = ((iv * 100 - minIV) / (maxIV - minIV || 1)) * 1.5 - 0.5;
      const z = numExps > 1 ? (i / (numExps - 1)) * 2 - 1 : 0;

      const projected = project(x, z, y);
      points.push({
        ...projected,
        iv,
        strike: strikes?.[j] || 0,
        dte: row.dte,
        color: getIVColor(iv * 100, minIV, maxIV)
      });
    }
  }

  // Sort by z for proper rendering
  points.sort((a, b) => b.z - a.z);

  return (
    <div
      style={{
        background: 'rgba(15, 23, 42, 0.8)',
        borderRadius: '0.75rem',
        overflow: 'hidden',
        cursor: 'grab',
        touchAction: 'none'
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onWheel={onWheel}
      onTouchStart={onMouseDown}
      onTouchMove={onMouseMove}
      onTouchEnd={onMouseUp}
    >
      {/* Mobile hint */}
      {isMobile && (
        <div style={{
          padding: '0.5rem',
          textAlign: 'center',
          fontSize: '0.65rem',
          color: '#64748b',
          borderBottom: '1px solid rgba(71, 85, 105, 0.3)'
        }}>
          <MousePointer size={12} style={{ display: 'inline', marginRight: '4px' }} />
          Drag to rotate • Tap points for details
        </div>
      )}
      <svg width="100%" height={isMobile ? 350 : 500} viewBox={`0 0 ${width} ${height}`}>
        {/* Grid lines */}
        <g opacity="0.2">
          {/* X-axis grid */}
          {Array.from({ length: 5 }).map((_, i) => {
            const x = -1 + i * 0.5;
            const start = project(x, -1, -0.5);
            const end = project(x, 1, -0.5);
            return (
              <line
                key={`x-${i}`}
                x1={start.x}
                y1={start.y}
                x2={end.x}
                y2={end.y}
                stroke="#64748b"
                strokeWidth="1"
              />
            );
          })}
          {/* Z-axis grid */}
          {Array.from({ length: 5 }).map((_, i) => {
            const z = -1 + i * 0.5;
            const start = project(-1, z, -0.5);
            const end = project(1, z, -0.5);
            return (
              <line
                key={`z-${i}`}
                x1={start.x}
                y1={start.y}
                x2={end.x}
                y2={end.y}
                stroke="#64748b"
                strokeWidth="1"
              />
            );
          })}
        </g>

        {/* Surface points */}
        {points.map((point, idx) => {
          const isHovered = hoveredPoint && 
            hoveredPoint.strike === point.strike && 
            hoveredPoint.dte === point.dte;
          return (
            <circle
              key={idx}
              cx={point.x}
              cy={point.y}
              r={Math.max(isMobile ? 5 : 3, (isMobile ? 8 : 6) * zoom)}
              fill={point.color}
              opacity={isHovered ? 1 : 0.8}
              stroke={isHovered ? '#fff' : 'none'}
              strokeWidth={isHovered ? 2 : 0}
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                setHoveredPoint(isHovered ? null : point);
              }}
              onMouseEnter={() => !isMobile && setHoveredPoint(point)}
              onMouseLeave={() => !isMobile && setHoveredPoint(null)}
            />
          );
        })}

        {/* Axis labels */}
        <text x={centerX} y={height - 20} fill="#94a3b8" fontSize={isMobile ? 10 : 12} textAnchor="middle">
          Strike Price →
        </text>
        <text x={30} y={centerY} fill="#94a3b8" fontSize={isMobile ? 10 : 12} textAnchor="middle" transform={`rotate(-90, 30, ${centerY})`}>
          IV →
        </text>
        <text x={width - 50} y={height - 50} fill="#94a3b8" fontSize={isMobile ? 10 : 12} textAnchor="middle">
          DTE →
        </text>
      </svg>
    </div>
  );
};

const HeatmapView = ({ data, getIVColor, surfaceStats, isMobile, hoveredPoint, setHoveredPoint }) => {
  if (!data?.surface || !surfaceStats) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No data available</div>;
  }

  const { surface, strikes } = data;
  const { minIV, maxIV } = surfaceStats;
  const validSurface = surface.filter(row => row && Array.isArray(row.ivs));
  const numStrikes = Math.min(strikes?.length || 0, isMobile ? 15 : 25);

  return (
    <div style={{
      background: 'rgba(15, 23, 42, 0.8)',
      borderRadius: '0.75rem',
      padding: isMobile ? '0.5rem' : '1rem',
      overflowX: 'auto'
    }}>
      {/* Mobile hint */}
      {isMobile && (
        <div style={{
          padding: '0.5rem',
          textAlign: 'center',
          fontSize: '0.65rem',
          color: '#64748b',
          marginBottom: '0.5rem'
        }}>
          <MousePointer size={12} style={{ display: 'inline', marginRight: '4px' }} />
          Tap cells for IV details
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '300px' : '600px' }}>
        <thead>
          <tr>
            <th style={{ padding: isMobile ? '0.25rem' : '0.5rem', color: '#94a3b8', fontSize: isMobile ? '0.6rem' : '0.75rem', textAlign: 'left' }}>DTE</th>
            {strikes?.slice(0, numStrikes).map((strike, idx) => (
              <th key={idx} style={{ padding: '0.25rem', color: '#94a3b8', fontSize: isMobile ? '0.5rem' : '0.65rem', textAlign: 'center' }}>
                {isMobile ? strike?.toFixed(0) : `$${strike?.toFixed(0)}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {validSurface.slice(0, isMobile ? 7 : 10).map((row, rowIdx) => (
            <tr key={rowIdx}>
              <td style={{ padding: isMobile ? '0.25rem' : '0.5rem', color: '#cbd5e1', fontSize: isMobile ? '0.65rem' : '0.8rem', fontWeight: '600' }}>
                {row.dte}d
              </td>
              {(row.ivs || []).slice(0, numStrikes).map((iv, colIdx) => {
                const isHovered = hoveredPoint && 
                  hoveredPoint.strike === strikes?.[colIdx] && 
                  hoveredPoint.dte === row.dte;
                return (
                  <td
                    key={colIdx}
                    style={{
                      padding: '0.25rem',
                      textAlign: 'center',
                      background: iv > 0 ? getIVColor(iv * 100, minIV, maxIV) : 'transparent',
                      color: 'white',
                      fontSize: isMobile ? '0.55rem' : '0.65rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      outline: isHovered ? '2px solid #fff' : 'none'
                    }}
                    onClick={() => setHoveredPoint(isHovered ? null : { 
                      iv, 
                      strike: strikes?.[colIdx], 
                      dte: row.dte,
                      color: getIVColor(iv * 100, minIV, maxIV)
                    })}
                    onMouseEnter={() => !isMobile && iv > 0 && setHoveredPoint({ 
                      iv, 
                      strike: strikes?.[colIdx], 
                      dte: row.dte 
                    })}
                    onMouseLeave={() => !isMobile && setHoveredPoint(null)}
                  >
                    {iv > 0 ? (iv * 100).toFixed(0) : '-'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SkewView = ({ data, selectedExpiration, setSelectedExpiration, isMobile, hoveredPoint, setHoveredPoint }) => {
  if (!data?.surface) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No data available</div>;
  }

  const { surface, strikes, currentPrice } = data;
  const validSurface = surface.filter(row => row && Array.isArray(row.ivs));
  const selectedRow = validSurface.find(r => r.expiration === selectedExpiration) || validSurface[0];

  if (!selectedRow?.ivs || selectedRow.ivs.length === 0) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No skew data available</div>;
  }

  const validIVs = selectedRow.ivs.filter(iv => iv > 0);
  const maxIV = validIVs.length > 0 ? Math.max(...validIVs) * 100 : 50;
  const chartWidth = isMobile ? 400 : 800;
  const chartHeight = isMobile ? 250 : 300;

  return (
    <div style={{
      background: 'rgba(15, 23, 42, 0.8)',
      borderRadius: '0.75rem',
      padding: isMobile ? '1rem' : '1.5rem'
    }}>
      {/* Expiration selector */}
      <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <label style={{ fontSize: isMobile ? '0.65rem' : '0.75rem', color: '#94a3b8' }}>Expiration:</label>
        <select
          value={selectedExpiration || ''}
          onChange={(e) => setSelectedExpiration(e.target.value)}
          style={{
            padding: isMobile ? '0.375rem' : '0.5rem',
            background: '#1e293b',
            border: '1px solid #475569',
            borderRadius: '0.375rem',
            color: 'white',
            fontSize: isMobile ? '0.75rem' : '0.875rem'
          }}
        >
          {validSurface.map((row, idx) => (
            <option key={idx} value={row.expiration}>
              {row.expiration} ({row.dte} DTE)
            </option>
          ))}
        </select>
      </div>

      {/* Mobile hint */}
      {isMobile && (
        <div style={{
          padding: '0.5rem',
          textAlign: 'center',
          fontSize: '0.65rem',
          color: '#64748b',
          marginBottom: '0.5rem'
        }}>
          <MousePointer size={12} style={{ display: 'inline', marginRight: '4px' }} />
          Tap points for strike/IV details
        </div>
      )}

      {/* Skew chart */}
      <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
        {/* Grid */}
        {Array.from({ length: 5 }).map((_, i) => (
          <line
            key={i}
            x1={50}
            y1={40 + i * ((chartHeight - 80) / 4)}
            x2={chartWidth - 50}
            y2={40 + i * ((chartHeight - 80) / 4)}
            stroke="#334155"
            strokeWidth="1"
          />
        ))}

        {/* Current price line */}
        {currentPrice && (
          <line
            x1={chartWidth / 2}
            y1={30}
            x2={chartWidth / 2}
            y2={chartHeight - 30}
            stroke="#60a5fa"
            strokeWidth="2"
            strokeDasharray="5,5"
          />
        )}

        {/* IV curve */}
        <path
          d={selectedRow.ivs.map((iv, idx) => {
            if (iv <= 0) return '';
            const x = 50 + (idx / Math.max(selectedRow.ivs.length - 1, 1)) * (chartWidth - 100);
            const y = chartHeight - 30 - (iv * 100 / maxIV) * (chartHeight - 80);
            return `${idx === 0 || selectedRow.ivs.slice(0, idx).every(v => v <= 0) ? 'M' : 'L'} ${x} ${y}`;
          }).filter(Boolean).join(' ')}
          fill="none"
          stroke="#8b5cf6"
          strokeWidth={isMobile ? 2 : 3}
        />

        {/* Data points */}
        {selectedRow.ivs.map((iv, idx) => {
          if (iv <= 0) return null;
          const x = 50 + (idx / Math.max(selectedRow.ivs.length - 1, 1)) * (chartWidth - 100);
          const y = chartHeight - 30 - (iv * 100 / maxIV) * (chartHeight - 80);
          const strike = strikes?.[idx];
          const isHovered = hoveredPoint && hoveredPoint.strike === strike;
          return (
            <circle
              key={idx}
              cx={x}
              cy={y}
              r={isHovered ? (isMobile ? 8 : 6) : (isMobile ? 6 : 4)}
              fill="#8b5cf6"
              stroke={isHovered ? '#fff' : 'none'}
              strokeWidth={isHovered ? 2 : 0}
              style={{ cursor: 'pointer' }}
              onClick={() => setHoveredPoint(isHovered ? null : { iv, strike, dte: selectedRow.dte })}
              onMouseEnter={() => !isMobile && setHoveredPoint({ iv, strike, dte: selectedRow.dte })}
              onMouseLeave={() => !isMobile && setHoveredPoint(null)}
            />
          );
        })}

        {/* X-axis labels */}
        <text x={50} y={chartHeight - 10} fill="#94a3b8" fontSize={isMobile ? 8 : 10}>OTM Puts</text>
        <text x={chartWidth / 2} y={chartHeight - 10} fill="#60a5fa" fontSize={isMobile ? 8 : 10} textAnchor="middle">ATM</text>
        <text x={chartWidth - 50} y={chartHeight - 10} fill="#94a3b8" fontSize={isMobile ? 8 : 10} textAnchor="end">OTM Calls</text>

        {/* Y-axis label */}
        <text x={15} y={chartHeight / 2} fill="#94a3b8" fontSize={isMobile ? 8 : 10} textAnchor="middle" transform={`rotate(-90, 15, ${chartHeight / 2})`}>
          IV %
        </text>
      </svg>
    </div>
  );
};

// ============================================
// EDUCATION PANEL
// ============================================

const VolSurfaceEducation = ({ onClose, isMobile }) => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
    borderBottom: '1px solid rgba(16, 185, 129, 0.3)',
    padding: isMobile ? '1rem' : '1.5rem 2rem'
  }}>
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '1rem' 
    }}>
      <h3 style={{ 
        margin: 0, 
        fontSize: isMobile ? '1rem' : '1.25rem', 
        color: '#10b981',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <BookOpen size={isMobile ? 18 : 22} />
        Understanding Volatility Surfaces
      </h3>
      <button
        onClick={onClose}
        style={{
          background: 'rgba(239, 68, 68, 0.2)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '50%',
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#ef4444'
        }}
      >
        <X size={16} />
      </button>
    </div>
    
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '0.75rem' : '1rem'
    }}>
      {/* What is IV? */}
      <div style={{
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '0.75rem',
        padding: isMobile ? '0.75rem' : '1rem'
      }}>
        <h4 style={{ 
          margin: '0 0 0.5rem 0', 
          fontSize: isMobile ? '0.8rem' : '0.9rem', 
          color: '#60a5fa',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <TrendingUp size={16} />
          What is Implied Volatility?
        </h4>
        <p style={{ 
          margin: 0, 
          fontSize: isMobile ? '0.7rem' : '0.8rem', 
          color: '#94a3b8',
          lineHeight: '1.5'
        }}>
          IV is the market's expectation of future price movement. Higher IV means options are more expensive 
          because traders expect bigger price swings. It's derived from option prices using models like Black-Scholes.
        </p>
      </div>

      {/* Volatility Smile */}
      <div style={{
        background: 'rgba(139, 92, 246, 0.1)',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        borderRadius: '0.75rem',
        padding: isMobile ? '0.75rem' : '1rem'
      }}>
        <h4 style={{ 
          margin: '0 0 0.5rem 0', 
          fontSize: isMobile ? '0.8rem' : '0.9rem', 
          color: '#a78bfa',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Activity size={16} />
          The Volatility Smile
        </h4>
        <p style={{ 
          margin: 0, 
          fontSize: isMobile ? '0.7rem' : '0.8rem', 
          color: '#94a3b8',
          lineHeight: '1.5'
        }}>
          IV typically forms a "smile" across strikes - higher for OTM puts and calls. This reflects 
          market fear of tail events. Puts often have higher IV than calls (the "skew"), pricing in 
          crash risk.
        </p>
      </div>

      {/* Term Structure */}
      <div style={{
        background: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '0.75rem',
        padding: isMobile ? '0.75rem' : '1rem'
      }}>
        <h4 style={{ 
          margin: '0 0 0.5rem 0', 
          fontSize: isMobile ? '0.8rem' : '0.9rem', 
          color: '#f59e0b',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Eye size={16} />
          Term Structure
        </h4>
        <p style={{ 
          margin: 0, 
          fontSize: isMobile ? '0.7rem' : '0.8rem', 
          color: '#94a3b8',
          lineHeight: '1.5'
        }}>
          IV varies by expiration. Near-term IV often spikes before events (earnings, Fed). 
          "Backwardation" (near > far IV) signals fear. "Contango" (far > near) is normal 
          in calm markets.
        </p>
      </div>
    </div>

    {/* Trading tip */}
    <div style={{
      marginTop: '1rem',
      padding: isMobile ? '0.75rem' : '1rem',
      background: 'rgba(16, 185, 129, 0.15)',
      border: '1px solid rgba(16, 185, 129, 0.3)',
      borderRadius: '0.75rem',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem'
    }}>
      <Lightbulb size={20} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
      <div>
        <strong style={{ color: '#10b981', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>Trading Tip:</strong>
        <span style={{ color: '#94a3b8', fontSize: isMobile ? '0.7rem' : '0.8rem', marginLeft: '0.5rem' }}>
          Look for options where IV deviates from the surface - they may be mispriced. 
          Sell high IV, buy low IV relative to the surface. Calendar spreads profit from term structure anomalies.
        </span>
      </div>
    </div>
  </div>
);

// Add spin animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin {
    animation: spin 1s linear infinite;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}

export default VolatilitySurface3D;